import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield } from 'lucide-react';
import type { TechTennisConfig } from '../types/competition';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  config?: TechTennisConfig;
  canEdit?: boolean;
  onSave?: (text: string) => void;
}

export const RankingRulesModal: React.FC<Props> = ({ isOpen, onClose, config, canEdit, onSave }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [text, setText] = React.useState(config?.customRulesText || '');

  React.useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setText(config?.customRulesText || '');
    }
  }, [isOpen, config?.customRulesText]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(4px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: "500px",
            backgroundColor: "#1A112C",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "var(--primary-color)",
              padding: "20px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Shield size={24} color="#2D1E4B" />
              <h2 style={{ color: "#2D1E4B", fontSize: "20px", fontWeight: 700, margin: 0 }}>
                Regulamento do Ranking
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#2D1E4B", cursor: "pointer", display: "flex" }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '60vh', overflowY: 'auto' }}>
            {isEditing ? (
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Digite o regulamento personalizado aqui..."
                style={{
                  width: '100%',
                  minHeight: '250px',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '16px',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  resize: 'vertical'
                }}
              />
            ) : config?.customRulesText ? (
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {config.customRulesText}
              </div>
            ) : (
              <>
                <div>
                  <section>
                    <h3 style={{ color: 'white', marginBottom: '8px', fontSize: '16px' }}>1. Sistema de Pontuação</h3>
                    <ul style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.5', paddingLeft: '20px' }}>
                      <li>Vitória: +{config?.pointsConfig?.winner || 10} pontos.</li>
                      <li>Derrota: {config?.pointsConfig?.loser || -5} pontos.</li>
                      <li>Vitória por W.O.: +{config?.pointsConfig?.woWinner || 5} pontos.</li>
                      <li>Derrota por W.O.: {config?.pointsConfig?.woLoser || -10} pontos.</li>
                    </ul>
                  </section>
                </div>
                <div>
                  <h3 style={{ color: 'white', marginBottom: '8px', fontSize: '16px' }}>2. Desafios</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.5' }}>
                    {config?.ladderRules?.challengeWindowAbove === 0 
                      ? "Você pode desafiar qualquer jogador sem restrição de posições." 
                      : `Você pode desafiar jogadores que estejam até ${config?.ladderRules?.challengeWindowAbove || 3} posições acima ou abaixo de você.`}
                    {' '}O desafiado tem até {config?.ladderRules?.acceptDeadlineDays || 3} dias para aceitar o desafio, ou perderá por W.O.
                  </p>
                </div>
                <div>
                  <h3 style={{ color: 'white', marginBottom: '8px', fontSize: '16px' }}>3. Penalidade por Inatividade</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.5' }}>
                    Jogadores inativos por mais de {config?.ladderRules?.inactivityDaysLimit || 30} dias sofrerão penalidades no ranking.
                  </p>
                </div>
                <div>
                  <h3 style={{ color: 'white', marginBottom: '8px', fontSize: '16px' }}>4. Atualização</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.5' }}>
                    O ranking é atualizado automaticamente após a validação do placar por ambos os jogadores.
                  </p>
                </div>
              </>
            )}
          </div>
          
          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            {isEditing ? (
              <>
                <button 
                  onClick={() => { setIsEditing(false); setText(config?.customRulesText || ''); }}
                  style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => { if (onSave) onSave(text); }}
                  style={{ padding: '10px 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Salvar
                </button>
              </>
            ) : (
              <>
                {canEdit && (
                  <button 
                    onClick={() => {
                      if (!text && !config?.customRulesText) {
                        const defaultText = `1. Sistema de Pontuação\n- Vitória: +${config?.pointsConfig?.winner || 10} pontos.\n- Derrota: ${config?.pointsConfig?.loser || -5} pontos.\n- Vitória por W.O.: +${config?.pointsConfig?.woWinner || 5} pontos.\n- Derrota por W.O.: ${config?.pointsConfig?.woLoser || -10} pontos.\n\n2. Desafios\n${config?.ladderRules?.challengeWindowAbove === 0 ? "Você pode desafiar qualquer jogador sem restrição de posições." : `Você pode desafiar jogadores que estejam até ${config?.ladderRules?.challengeWindowAbove || 3} posições acima ou abaixo de você.`} O desafiado tem até ${config?.ladderRules?.acceptDeadlineDays || 3} dias para aceitar o desafio, ou perderá por W.O.\n\n3. Penalidade por Inatividade\nJogadores inativos por mais de ${config?.ladderRules?.inactivityDaysLimit || 30} dias sofrerão penalidades no ranking.\n\n4. Atualização\nO ranking é atualizado automaticamente após a validação do placar por ambos os jogadores.`;
                        setText(defaultText);
                      }
                      setIsEditing(true);
                    }}
                    style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Editar Regulamento
                  </button>
                )}
                <button 
                  onClick={onClose}
                  style={{ padding: '10px 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Entendi
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
